import axios from "axios";
import * as actionTypes from "./actionTypes";
import { GET_HOME_CATEGORIES_URL, GET_ALL_CATEGORIES_URL, GET_CATEGORY_BY_SLUG_URL, GET_CATEGORIES_WITH_PRODUCTS_URL, SEARCH_CATEGORIES_URL, GET_CATEGORY_TREE_URL, GET_CATEGORY_ITEMS_URL } from "../../configs";
import { categoriesData } from "../../components/Mobile/Phase2/Home/Categories/categoryData";
import { getImageUrl } from "../../helpers/imageUrl";
import { CATEGORY_PLACEHOLDER } from "../../helpers/categoryPlaceholder";

// Helper function to get location data from localStorage or use defaults
const getLocationData = () => {
  try {
    const savedLocation = localStorage.getItem('user_location');
    if (savedLocation) {
      const location = JSON.parse(savedLocation);
      return {
        latitude: location.latitude || 15.3172775,
        longitude: location.longitude || 75.7138884,
        state: location.state || 'Karnataka'
      };
    }
  } catch (error) {
    console.log('Error parsing location data:', error);
  }

  // Default location (Bangalore, Karnataka)
  return {
    latitude: 15.3172775,
    longitude: 75.7138884,
    state: 'Karnataka'
  };
};

// Transform API response to match our data structure
export const transformCategoryData = (apiCategories) => {
  return apiCategories.map(category => ({
    id: category.id,
    name: category.name,
    slug: category.name.toLowerCase().replace(/\s+/g, '-').replace(/[()&,]/g, ''),
    image: category.image,
    placeholder: category.image ?
      getImageUrl(category.image) :
      CATEGORY_PLACEHOLDER,
    isHomeCategory: true,
    subcategories: [] // Will be populated when needed
  }));
};

// Get Home Page Categories (for carousel)
export const getHomeCategories = () => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.GET_HOME_CATEGORIES_REQUEST });

    try {
      const { latitude, longitude, state } = getLocationData();
      const apiUrl = `${GET_HOME_CATEGORIES_URL}?latitude=${latitude}&longitude=${longitude}&state=${state}&main-categories-16`;

      const response = await axios.get(apiUrl);

      if (response.data.success && response.data.main_categories_16) {
        // Only the categories the API returned are shown: padding the list with the static sample
        // categories mixed fake names and stock photos in with the real ones.
        const finalCategories = transformCategoryData(response.data.main_categories_16);

        dispatch({
          type: actionTypes.GET_HOME_CATEGORIES_SUCCESS,
          payload: finalCategories
        });
        return { success: true, data: finalCategories };
      } else {
        // Fallback to mock data if API fails
        const mockCategories = categoriesData.filter(cat => cat.isHomeCategory);
        dispatch({
          type: actionTypes.GET_HOME_CATEGORIES_SUCCESS,
          payload: mockCategories
        });
        return { success: true, data: mockCategories };
      }
    } catch (error) {
      console.error('❌ Home categories API error:', error);
      const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || "Network error";

      // Fallback to mock data on error
      const mockCategories = categoriesData.filter(cat => cat.isHomeCategory);
      dispatch({
        type: actionTypes.GET_HOME_CATEGORIES_SUCCESS,
        payload: mockCategories
      });

      return { success: true, data: mockCategories };
    }
  };
};

// Get All Categories (for All Categories page)
export const getAllCategories = () => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.GET_ALL_CATEGORIES_REQUEST });

    try {
      const { latitude, longitude, state } = getLocationData();
      const apiUrl = `${GET_ALL_CATEGORIES_URL}?latitude=${latitude}&longitude=${longitude}&state=${state}&all-main-categories`;

      const response = await axios.get(apiUrl);

      if (response.data.success && response.data.all_main_categories) {
        const transformedCategories = transformCategoryData(response.data.all_main_categories);

        dispatch({
          type: actionTypes.GET_ALL_CATEGORIES_SUCCESS,
          payload: transformedCategories
        });
        return { success: true, data: transformedCategories };
      } else {
        // Fallback to mock data if API response is invalid
        const mockCategories = categoriesData;
        dispatch({
          type: actionTypes.GET_ALL_CATEGORIES_SUCCESS,
          payload: mockCategories
        });
        return { success: true, data: mockCategories };
      }
    } catch (error) {
      console.error('❌ All categories API error:', error);
      const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || "Network error";

      // Fallback to mock data on error
      const mockCategories = categoriesData;
      dispatch({
        type: actionTypes.GET_ALL_CATEGORIES_SUCCESS,
        payload: mockCategories
      });

      return { success: true, data: mockCategories };
    }
  };
};

// Transform subcategory data from API response
const transformSubcategoryData = (apiSubcategories) => {
  return apiSubcategories.map(subcategory => ({
    id: subcategory.id,
    name: subcategory.name,
    slug: subcategory.name.toLowerCase().replace(/\s+/g, '-').replace(/[()&,]/g, ''),
    image: subcategory.image,
    placeholder: subcategory.image ?
      getImageUrl(subcategory.image) :
      CATEGORY_PLACEHOLDER
  }));
};

// Get Category by Slug (for subcategories page)
export const getCategoryBySlug = (slug) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.GET_CATEGORY_BY_SLUG_REQUEST });

    try {
      const { latitude, longitude, state } = getLocationData();
      const apiUrl = `${GET_CATEGORY_BY_SLUG_URL}?latitude=${latitude}&longitude=${longitude}&state=${state}&main-categories-with-subs`;

      const response = await axios.get(apiUrl);

      if (response.data.success && response.data.categories_with_subcategories) {
        // Find the category that matches the slug
        const matchingCategory = response.data.categories_with_subcategories.find(category => {
          const categorySlug = category.name.toLowerCase().replace(/\s+/g, '-').replace(/[()&,]/g, '');
          return categorySlug === slug;
        });

        if (matchingCategory) {
          const transformedSubcategories = transformSubcategoryData(matchingCategory.subcategories || []);

          const categoryData = {
            id: matchingCategory.id,
            name: matchingCategory.name,
            slug: slug,
            image: matchingCategory.image,
            placeholder: matchingCategory.image ?
              getImageUrl(matchingCategory.image) :
              CATEGORY_PLACEHOLDER,
            subcategories: transformedSubcategories
          };

          dispatch({
            type: actionTypes.GET_CATEGORY_BY_SLUG_SUCCESS,
            payload: categoryData
          });
          return { success: true, data: categoryData };
        } else {
          // Fallback to mock data if category not found
          const mockCategory = categoriesData.find(cat => cat.slug === slug);
          if (mockCategory) {
            dispatch({
              type: actionTypes.GET_CATEGORY_BY_SLUG_SUCCESS,
              payload: mockCategory
            });
            return { success: true, data: mockCategory };
          } else {
            dispatch({
              type: actionTypes.GET_CATEGORY_BY_SLUG_FAILURE,
              payload: "Category not found"
            });
            return { success: false, message: "Category not found" };
          }
        }
      } else {
        // Fallback to mock data if API response is invalid
        const mockCategory = categoriesData.find(cat => cat.slug === slug);
        if (mockCategory) {
          dispatch({
            type: actionTypes.GET_CATEGORY_BY_SLUG_SUCCESS,
            payload: mockCategory
          });
          return { success: true, data: mockCategory };
        } else {
          dispatch({
            type: actionTypes.GET_CATEGORY_BY_SLUG_FAILURE,
            payload: "Category not found"
          });
          return { success: false, message: "Category not found" };
        }
      }
    } catch (error) {
      console.error('❌ Category by slug API error:', error);
      const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || "Network error";

      // Fallback to mock data on error
      const mockCategory = categoriesData.find(cat => cat.slug === slug);
      if (mockCategory) {
        dispatch({
          type: actionTypes.GET_CATEGORY_BY_SLUG_SUCCESS,
          payload: mockCategory
        });
        return { success: true, data: mockCategory };
      } else {
        dispatch({
          type: actionTypes.GET_CATEGORY_BY_SLUG_FAILURE,
          payload: errorMessage
        });
        return { success: false, message: errorMessage };
      }
    }
  };
};

// Search Categories (fallback to mock data for now)
export const searchCategories = (searchTerm) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.SEARCH_CATEGORIES_REQUEST });

    try {
      // TODO: Replace with real API when available
      // const response = await axios.get(`${SEARCH_CATEGORIES_URL}?q=${searchTerm}`);

      // For now, use mock data search
      const filteredCategories = categoriesData.filter(cat =>
        cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (cat.subcategories && cat.subcategories.some(sub =>
          sub.name.toLowerCase().includes(searchTerm.toLowerCase())
        ))
      );

      dispatch({
        type: actionTypes.SEARCH_CATEGORIES_SUCCESS,
        payload: filteredCategories
      });
      return { success: true, data: filteredCategories };
    } catch (error) {
      const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || "Network error";
      dispatch({
        type: actionTypes.SEARCH_CATEGORIES_FAILURE,
        payload: errorMessage
      });
      return { success: false, message: errorMessage };
    }
  };
};

// Clear Categories Error
export const clearCategoriesError = () => ({
  type: actionTypes.CLEAR_CATEGORIES_ERROR
});

// Reset Categories State
export const resetCategoriesState = () => ({
  type: actionTypes.RESET_CATEGORIES_STATE
});

// Transform product data from API response
const transformProductData = (apiProducts) => {
  return apiProducts.map((product, index) => {
    // Use real old_price from API (no more mock data)
    const old_price = product.old_price || null;

    // Extract restaurant_id from nested restaurant object
    const restaurant_id = product.restaurant ? product.restaurant.id : null;

    // Extract item_category_id from nested item_category object
    const item_category_id = product.item_category ? product.item_category.id : null;

    return {
      id: product.id,
      name: product.name,
      price: product.price,
      old_price: old_price,
      image: product.image,
      placeholder: product.image ?
        getImageUrl(product.image) :
        getImageUrl('/assets/img/items/placeholder.jpg'),
      brand: product.brand || '',
      description: product.description || '',
      is_active: product.is_active !== undefined ? product.is_active : true,
      stock_status: product.stock_status || 'available',
      is_favorited: product.is_favorited || false,
      addon_categories: product.addon_categories || [],
      stock: product.stock || 100,
      quantity: 1,
      // Include additional fields from API response
      is_veg: product.is_veg,
      restaurant: product.restaurant,
      item_category: product.item_category,
      master_item: product.master_item,
      // EXTRACT NESTED IDs FOR CART FUNCTIONALITY
      restaurant_id: restaurant_id, // Extract from product.restaurant.id
      item_category_id: item_category_id, // Extract from product.item_category.id
      zone_id: product.zone_id || null, // Use zone_id from API or null
      // Convert boolean badge values to numeric (ItemBadge expects 1/0, not true/false)
      is_new: product.is_new ? 1 : 0,
      is_top_picks: product.is_top_picks ? 1 : 0,
      is_popular: product.is_popular ? 1 : 0,
      tags: product.tags || []
    };
  });
};

// Get Categories with Products (for products display page)
export const getCategoriesWithProducts = () => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.GET_CATEGORIES_WITH_PRODUCTS_REQUEST });

    try {
      const { latitude, longitude, state } = getLocationData();
      const apiUrl = `${GET_CATEGORIES_WITH_PRODUCTS_URL}?latitude=${latitude}&longitude=${longitude}&state=${state}&categories-with-products`;

      const response = await axios.get(apiUrl);

      if (response.data.success && response.data.categories_with_subcategories_and_products) {
        // Transform the data: subcategory.products = direct only (items without sub-sub); sub_subcategories keep their own products (3-level API)
        const transformedCategories = response.data.categories_with_subcategories_and_products.map(category => ({
          id: category.id,
          name: category.name,
          slug: category.name.toLowerCase().replace(/\s+/g, '-').replace(/[()&,]/g, ''),
          image: category.image,
          placeholder: category.image ?
            getImageUrl(category.image) :
            CATEGORY_PLACEHOLDER,
          subcategories: category.subcategories.map(subcategory => {
            const directProducts = subcategory.products || [];
            return {
              id: subcategory.id,
              name: subcategory.name,
              slug: subcategory.name.toLowerCase().replace(/\s+/g, '-').replace(/[()&,]/g, ''),
              image: subcategory.image,
              placeholder: subcategory.image ?
                getImageUrl(subcategory.image) :
                CATEGORY_PLACEHOLDER,
              products: transformProductData(directProducts),
              sub_subcategories: (subcategory.sub_subcategories || []).map(ss => ({
                id: ss.id,
                name: ss.name,
                slug: ss.name.toLowerCase().replace(/\s+/g, '-').replace(/[()&,]/g, ''),
                products: transformProductData(ss.products || [])
              }))
            };
          })
        }));

        dispatch({
          type: actionTypes.GET_CATEGORIES_WITH_PRODUCTS_SUCCESS,
          payload: transformedCategories
        });
        return { success: true, data: transformedCategories };
      } else {
        dispatch({
          type: actionTypes.GET_CATEGORIES_WITH_PRODUCTS_FAILURE,
          payload: "Invalid API response"
        });
        return { success: false, message: "Invalid API response" };
      }
    } catch (error) {
      console.error('❌ Categories with products API error:', error);
      const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || "Network error";

      dispatch({
        type: actionTypes.GET_CATEGORIES_WITH_PRODUCTS_FAILURE,
        payload: errorMessage
      });
      return { success: false, message: errorMessage };
    }
  };
};

// Get full L1 -> L2 -> L3 category tree with per-node item counts, for the
// Categories browse page (/categories/stores). `mode` defaults to 'sprint'
// since that page is the Sprint/quick-commerce category browser.
export const getCategoryTree = (mode = 'sprint') => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.GET_CATEGORY_TREE_REQUEST });

    try {
      const { latitude, longitude, state } = getLocationData();
      const apiUrl = `${GET_CATEGORY_TREE_URL}?latitude=${latitude}&longitude=${longitude}&state=${state}&mode=${mode}&category-tree=1`;

      const response = await axios.get(apiUrl);

      if (response.data.success) {
        const tree = response.data.tree || [];
        dispatch({
          type: actionTypes.GET_CATEGORY_TREE_SUCCESS,
          payload: tree
        });
        return { success: true, data: tree };
      } else {
        dispatch({
          type: actionTypes.GET_CATEGORY_TREE_FAILURE,
          payload: response.data.message || "Failed to load categories"
        });
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('❌ Category tree API error:', error);
      const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || "Network error";
      dispatch({
        type: actionTypes.GET_CATEGORY_TREE_FAILURE,
        payload: errorMessage
      });
      return { success: false, message: errorMessage };
    }
  };
};

// Get products for a single category-tree node (L1, L2, or L3). Used when the
// user taps a tile on the Categories browse page.
export const getCategoryItems = (categoryId, level, page = 1, sortBy = 'distance', mode = 'sprint') => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.GET_CATEGORY_ITEMS_REQUEST });

    try {
      const { latitude, longitude, state } = getLocationData();
      const apiUrl = `${GET_CATEGORY_ITEMS_URL}?latitude=${latitude}&longitude=${longitude}&state=${state}&mode=${mode}&category_id=${categoryId}&category_level=${level}&page=${page}&sort_by=${sortBy}`;

      const response = await axios.get(apiUrl);

      if (response.data.success) {
        const payload = {
          items: response.data.data || [],
          total: response.data.total || 0,
          page: response.data.page || page,
          perPage: response.data.per_page || 20,
          append: page > 1,
        };
        dispatch({
          type: actionTypes.GET_CATEGORY_ITEMS_SUCCESS,
          payload
        });
        return { success: true, ...payload };
      } else {
        dispatch({
          type: actionTypes.GET_CATEGORY_ITEMS_FAILURE,
          payload: response.data.message || "Failed to load products"
        });
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('❌ Category items API error:', error);
      const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || "Network error";
      dispatch({
        type: actionTypes.GET_CATEGORY_ITEMS_FAILURE,
        payload: errorMessage
      });
      return { success: false, message: errorMessage };
    }
  };
};

export const resetCategoryItems = () => ({
  type: actionTypes.RESET_CATEGORY_ITEMS
});
